import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as api from '@apis/reservation-success';
import {
  ApplicationStatus,
  ReservationGrade,
  ReservationSuccessProductIndicatorRow,
  ReservationSuccessProductIndicatorsQuery,
  ReservationSuccessProductIndicatorSortField,
} from '@apis/reservation-success';
import { Button, Card, Col, DatePicker, Input, Row, Space, Statistic, Table, Tag, Tooltip, message } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import {
  RESERVATION_GRADE_COLOR,
  RESERVATION_GRADE_HINT,
  RESERVATION_GRADE_LABEL,
} from '@utils/reservation-success-labels';
import { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import { SortOrder, SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import moment from 'moment';
import { useSearchParams } from 'react-router-dom';
import ReservationSuccessPageHeader from '@components/reservation-success/ReservationSuccessPageHeader';
const { RangePicker } = DatePicker;

const APPLICATION_STATUS_CONFIG: Record<ApplicationStatus, { label: string; color: string }> = {
  no_application: { label: '무신청', color: 'error' },
  stagnant: { label: '정체', color: 'warning' },
  active: { label: '활발', color: 'success' },
};

const GRADE_ORDER: ReservationGrade[] = ['danger', 'caution', 'recommended', 'stable'];

const GRADE_COLUMN_CONFIG: Array<{ grade: ReservationGrade; title: string; color: string }> = GRADE_ORDER.map(
  (grade) => ({
    grade,
    title: RESERVATION_GRADE_LABEL[grade],
    color: RESERVATION_GRADE_COLOR[grade],
  }),
);

const SUMMARY_ITEMS: Array<{ key: 'partnerCount' | 'productCount' | ReservationGrade; title: string; color?: string }> =
  [
    { key: 'partnerCount', title: '파트너 수' },
    { key: 'productCount', title: '상품 수' },
    ...GRADE_ORDER.map((grade) => ({
      key: grade,
      title: `${RESERVATION_GRADE_LABEL[grade]} 합계`,
      color: RESERVATION_GRADE_COLOR[grade],
    })),
  ];

const columnTitle = (label: string, tip: string) => <Tooltip title={tip}>{label}</Tooltip>;

const formatCancelRate = (value?: number) => (value != null ? `${Math.round(value)}%` : '-');

const getColumnSortOrder = (
  field: ReservationSuccessProductIndicatorSortField,
  searchQuery: ReservationSuccessProductIndicatorsQuery,
): SortOrder | undefined => {
  if (searchQuery.sortField !== field) return undefined;
  return searchQuery.sortOrder === 'asc' ? 'ascend' : 'descend';
};

const SORT_FIELDS: ReservationSuccessProductIndicatorSortField[] = [
  'partnerName',
  'productName',
  'bookings3d',
  'cancelRate1m',
  'cancelRate2m',
  'cancelRate3m',
  'danger',
  'caution',
  'recommended',
  'stable',
];

const ReservationSuccessProductIndicatorsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialKeyword = searchParams.get('keyword')?.trim() || '';
  const initialSortField = searchParams.get('sortField') as ReservationSuccessProductIndicatorSortField | null;
  const initialSortOrder = searchParams.get('sortOrder');
  const [keywordInput, setKeywordInput] = useState(initialKeyword);
  const [exporting, setExporting] = useState(false);
  const [searchQuery, setSearchQuery] = useState<ReservationSuccessProductIndicatorsQuery>({
    page: 0,
    size: 20,
    dateFrom: searchParams.get('dateFrom') || moment().format('YYYY-MM-DD'),
    dateTo: searchParams.get('dateTo') || moment().add(7, 'day').format('YYYY-MM-DD'),
    keyword: initialKeyword || undefined,
    sortField: initialSortField && SORT_FIELDS.includes(initialSortField) ? initialSortField : undefined,
    sortOrder: initialSortOrder === 'asc' || initialSortOrder === 'desc' ? initialSortOrder : undefined,
  });

  const listQuery = useQuery({
    queryKey: ['reservation-success-product-indicators', JSON.stringify(searchQuery)],
    queryFn: () => api.listReservationSuccessProductIndicators(searchQuery),
    refetchInterval: 60000,
  });

  const data = listQuery.data?.data;
  const rows = data?.contents || [];
  const summary = data?.summary;

  const getSummaryValue = (key: (typeof SUMMARY_ITEMS)[number]['key']) => {
    if (!summary) return 0;
    if (key === 'partnerCount') return summary.partnerCount;
    if (key === 'productCount') return summary.productCount;
    return summary.byGrade[key] ?? 0;
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await api.exportReservationSuccessSessionIndicatorsExcel({
        keyword: searchQuery.keyword,
        dateFrom: searchQuery.dateFrom,
        dateTo: searchQuery.dateTo,
      });
      const fileName = `igogo-reservation-success-sessions-${moment().format('YYYYMMDD-HHmmss')}.xlsx`;
      const url = window.URL.createObjectURL(blob.data);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success('세션별 엑셀 파일이 다운로드되었습니다.');
    } catch {
      message.error('엑셀 다운로드에 실패했습니다.');
    } finally {
      setExporting(false);
    }
  };

  const handleTableChange = (
    pagination: TablePaginationConfig,
    _filters: unknown,
    sorter: SorterResult<ReservationSuccessProductIndicatorRow> | SorterResult<ReservationSuccessProductIndicatorRow>[],
  ) => {
    const activeSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    const nextField = activeSorter?.field;
    const nextOrder = activeSorter?.order;

    let sortChanged = false;
    let nextSortField = searchQuery.sortField;
    let nextSortOrder = searchQuery.sortOrder;

    if (typeof nextField === 'string') {
      const field = nextField as ReservationSuccessProductIndicatorSortField;
      const order = nextOrder === 'ascend' ? 'asc' : nextOrder === 'descend' ? 'desc' : undefined;
      sortChanged = field !== searchQuery.sortField || order !== searchQuery.sortOrder;
      nextSortField = order ? field : undefined;
      nextSortOrder = order;
    } else if (!nextOrder && (searchQuery.sortField || searchQuery.sortOrder)) {
      sortChanged = true;
      nextSortField = undefined;
      nextSortOrder = undefined;
    }

    if (sortChanged) {
      setSearchQuery((prev) => ({
        ...prev,
        sortField: nextSortField,
        sortOrder: nextSortOrder,
        page: 0,
      }));
      return;
    }

    setSearchQuery((prev) => ({
      ...prev,
      page: (pagination.current || 1) - 1,
      size: pagination.pageSize || 20,
    }));
  };

  const columns: ColumnsType<ReservationSuccessProductIndicatorRow> = [
    {
      title: '파트너명',
      dataIndex: 'partnerName',
      key: 'partnerName',
      fixed: 'left',
      width: 140,
      sorter: true,
      sortOrder: getColumnSortOrder('partnerName', searchQuery),
      render: (value, row) => (
        <Tooltip title={row.partnerId}>
          <span>{value || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: '상품명',
      dataIndex: 'productName',
      key: 'productName',
      fixed: 'left',
      width: 220,
      ellipsis: true,
      sorter: true,
      sortOrder: getColumnSortOrder('productName', searchQuery),
      render: (value, row) => (
        <Tooltip title={row.productId}>
          <span>{value || row.productId}</span>
        </Tooltip>
      ),
    },
    {
      title: '진단',
      key: 'diagnosis',
      children: [
        {
          title: columnTitle(
            'D-day 재고소진',
            '수업일까지 남은 날 기준 재고 소진입니다. 선택 기간 안에서 가장 심각한 회차를 대표로 보여 줍니다.',
          ),
          dataIndex: 'dDayStockLabel',
          key: 'dDayStockLabel',
          width: 150,
          ellipsis: true,
          render: (value, row) => {
            if (!value) return '-';
            const tip = row.representativeSessionDate
              ? `${row.representativeSessionDate} 기준 (선택 기간 내 최심각 세션)`
              : '선택 기간 내 최심각 세션';
            return (
              <Tooltip title={tip}>
                <span>{value}</span>
              </Tooltip>
            );
          },
        },
        {
          title: columnTitle('종합진단', '대표 회차의 진단 문장입니다. 세션별 상세는 엑셀에서 확인합니다.'),
          dataIndex: 'combinedDiagnosis',
          key: 'combinedDiagnosis',
          width: 320,
          ellipsis: true,
          render: (value, row) => {
            if (!value) return '-';
            const tip = row.representativeSessionDate ? `${row.representativeSessionDate} · ${value}` : value;
            return (
              <Tooltip title={tip}>
                <span>{value}</span>
              </Tooltip>
            );
          },
        },
      ],
    },
    {
      title: '신청·취소',
      key: 'application',
      children: [
        {
          title: columnTitle(
            '최근 3일 신청',
            '무신청은 신청이 없고, 정체는 신청이 늘지 않으며, 활발은 신청이 이어지는 상태입니다.',
          ),
          key: 'bookings3d',
          width: 150,
          sorter: true,
          sortOrder: getColumnSortOrder('bookings3d', searchQuery),
          render: (_, row) => {
            const status = APPLICATION_STATUS_CONFIG[row.applicationStatus];
            return (
              <Space size={8}>
                <span>{row.bookings3d.toLocaleString()}건</span>
                {status && <Tag color={status.color}>{status.label}</Tag>}
              </Space>
            );
          },
        },
        {
          title: columnTitle('취소율 1개월', '최근 1개월 결제 대비 취소 비율입니다.'),
          dataIndex: 'cancelRate1m',
          key: 'cancelRate1m',
          width: 100,
          align: 'right',
          sorter: true,
          sortOrder: getColumnSortOrder('cancelRate1m', searchQuery),
          render: formatCancelRate,
        },
        {
          title: columnTitle('2개월', '최근 2개월 결제 대비 취소 비율입니다.'),
          dataIndex: 'cancelRate2m',
          key: 'cancelRate2m',
          width: 80,
          align: 'right',
          sorter: true,
          sortOrder: getColumnSortOrder('cancelRate2m', searchQuery),
          render: formatCancelRate,
        },
        {
          title: columnTitle('3개월', '최근 3개월 결제 대비 취소 비율입니다.'),
          dataIndex: 'cancelRate3m',
          key: 'cancelRate3m',
          width: 80,
          align: 'right',
          sorter: true,
          sortOrder: getColumnSortOrder('cancelRate3m', searchQuery),
          render: formatCancelRate,
        },
      ],
    },
    {
      title: '등급 수',
      key: 'grades',
      children: [
        ...GRADE_COLUMN_CONFIG.map(({ grade, title, color }) => ({
          title: columnTitle(title, RESERVATION_GRADE_HINT[grade]),
          key: grade,
          width: 90,
          align: 'center' as const,
          sorter: true,
          sortOrder: getColumnSortOrder(grade, searchQuery),
          render: (_: unknown, row: ReservationSuccessProductIndicatorRow) => {
            const count = row.byGrade[grade] ?? 0;
            return (
              <span style={{ color: count > 0 ? color : undefined, fontWeight: count > 0 ? 600 : 400 }}>{count}</span>
            );
          },
        })),
      ],
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <ReservationSuccessPageHeader
        title="상품 보조지표"
        description={
          <>
            선택한 수업일의 상품별 등급과 보조지표입니다. 등급 수는 라스트찬스에 등록하지 않은 진단 중 회차만 셉니다.
            세션별 상세는 엑셀에서 확인합니다. 데이터는 1시간마다 갱신됩니다.
            {data?.hourBucket ? ` (기준: ${data.hourBucket.replace('T', ' ')}시)` : ''}
          </>
        }
      />

      <Space wrap style={{ marginBottom: 16 }}>
        <Input.Search
          className={filterActiveClassName(!!searchQuery.keyword)}
          placeholder="파트너명 또는 상품명 검색"
          allowClear
          style={{ width: 280 }}
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onSearch={(value) => setSearchQuery((prev) => ({ ...prev, keyword: value.trim() || undefined, page: 0 }))}
        />
        <RangePicker
          value={
            searchQuery.dateFrom && searchQuery.dateTo
              ? [dayjs(searchQuery.dateFrom), dayjs(searchQuery.dateTo)]
              : undefined
          }
          onChange={(dates) => {
            if (!dates?.[0] || !dates?.[1]) return;
            setSearchQuery((prev) => ({
              ...prev,
              dateFrom: dates[0]!.format('YYYY-MM-DD'),
              dateTo: dates[1]!.format('YYYY-MM-DD'),
              page: 0,
            }));
          }}
        />
        <Button onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>
          새로고침
        </Button>
        <Button onClick={handleExport} loading={exporting}>
          세션 엑셀 다운로드
        </Button>
      </Space>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {SUMMARY_ITEMS.map((item) => (
          <Col key={item.key} xs={12} sm={8} md={6} lg={4}>
            <Card size="small">
              <Statistic
                title={item.title}
                value={getSummaryValue(item.key)}
                loading={listQuery.isLoading}
                valueStyle={item.color ? { color: item.color } : undefined}
              />
            </Card>
          </Col>
        ))}
      </Row>

      <Table
        rowKey={(row) => `${row.partnerId}:${row.productId}`}
        columns={columns}
        dataSource={rows}
        loading={listQuery.isLoading}
        scroll={{ x: 1700 }}
        pagination={{
          current: (data?.page || 0) + 1,
          pageSize: data?.size || 20,
          total: data?.total || 0,
          showSizeChanger: true,
        }}
        onChange={handleTableChange}
      />
    </div>
  );
};

export default ReservationSuccessProductIndicatorsPage;
